package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.background
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * One self-disclosure line on a profile — an icon plus already-localized text
 * (e.g. `"work"` + "Software engineer", `"home"` + "From Shiraz").
 *
 * [text] is pre-resolved by the caller: this is a design-system component and holds no copy
 * (golden rule 6). [iconName] is a Material Symbols ligature name.
 */
data class ProfileDetail(val iconName: String, val text: String)

/**
 * The shared "about this person" body: optional bio, a list of [ProfileDetail] rows, and interest
 * chips — the presentation used BOTH for someone else's profile (Find your people) and for your own
 * Profile tab.
 *
 * It lives here rather than in a feature module because a feature module may never depend on another
 * feature module (golden rule 5), and this is rendered from `:feature:chat` and `:feature:profile`.
 * Every string arrives resolved; empty sections drop out entirely rather than rendering a header over
 * nothing (same rule the skeletons follow).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ProfileAbout(
    bio: String?,
    details: List<ProfileDetail>,
    interests: List<String>,
    aboutTitle: String,
    detailsTitle: String,
    interestsTitle: String,
    modifier: Modifier = Modifier,
    /** Rendered under the bio when there is nothing to show yet (e.g. "Add a short bio…"). */
    emptyHint: String? = null,
) {
    val hasBio = !bio.isNullOrBlank()
    val isEmpty = !hasBio && details.isEmpty() && interests.isEmpty()
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
    ) {
        if (isEmpty) {
            if (emptyHint != null) {
                Text(
                    text = emptyHint,
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }
            return@Column
        }
        if (hasBio) {
            ProfileAboutSection(title = aboutTitle) {
                Text(
                    text = bio.orEmpty(),
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurface,
                )
            }
        }
        if (details.isNotEmpty()) {
            ProfileAboutSection(title = detailsTitle) {
                // One grouped surface with hairline separators, rather than a separate card per line —
                // four stacked cards read as unrelated blocks and doubled the vertical cost.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppTheme.colors.surface, AppTheme.shapes.card),
                ) {
                    details.forEachIndexed { index, detail ->
                        if (index > 0) {
                            HorizontalDivider(
                                color = AppTheme.colors.outlineVariant,
                                modifier = Modifier.padding(start = AppTheme.spacing.xxl),
                            )
                        }
                        ProfileDetailRow(detail)
                    }
                }
            }
        }
        if (interests.isNotEmpty()) {
            ProfileAboutSection(title = interestsTitle) {
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                ) {
                    // Read-only display chips: selection is meaningless here, so they no-op on tap.
                    interests.forEach { AppChip(label = it, selected = false, onClick = {}) }
                }
            }
        }
    }
}

@Composable
private fun ProfileAboutSection(title: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        Text(
            text = title,
            style = AppTheme.typography.labelMd,
            color = AppTheme.colors.onSurfaceVariant,
        )
        content()
    }
}

@Composable
private fun ProfileDetailRow(detail: ProfileDetail) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.md),
    ) {
        // Tinted round chip behind the glyph — gives the rows a consistent optical left edge and
        // reads as one list instead of four floating icons.
        Box(
            modifier = Modifier
                .size(AppTheme.sizes.avatarSm)
                .background(AppTheme.colors.categoryChip, AppTheme.shapes.pill),
            contentAlignment = Alignment.Center,
        ) {
            AppIcon(
                name = detail.iconName,
                // Decorative: the text beside it carries the meaning for screen readers.
                contentDescription = null,
                tint = AppTheme.colors.primary,
                modifier = Modifier.size(AppTheme.sizes.iconSm),
            )
        }
        Text(
            text = detail.text,
            style = AppTheme.typography.bodyMd,
            color = AppTheme.colors.onSurface,
        )
    }
}
